import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import useBodyScrollLock from "../hooks/useBodyScrollLock";
import { useNavItems } from "./NavItemsContext";
import { COLORS as C, RADIUS, MONO } from "../constants/ui";

/** Typing in a field should keep the browser's own Cmd+K, not open this. */
function isTypingTarget(el) {
  if (!el) return false;
  const tag = el.tagName;
  return (
    tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.isContentEditable === true
  );
}

/**
 * Score a destination against the query. Higher is better; 0 means no match.
 * Ranking matters because substrings collide — "board" is in both "Sprint
 * Board" and "Dashboard", and the one starting with it should come first.
 */
function scoreMatch(query, text) {
  const q = query.toLowerCase().trim();
  if (!q) return 1;
  const t = (text || "").toLowerCase();

  if (t === q) return 100;
  if (t.startsWith(q)) return 80;
  // Start of any word — "board" should favour "Sprint Board" over "Dashboard".
  if (t.split(/\s+/).some((w) => w.startsWith(q))) return 60;
  if (t.includes(q)) return 40;

  // Subsequence, so "spb" still finds "Sprint Board".
  let i = 0;
  for (const ch of t) {
    if (ch === q[i]) i += 1;
    if (i === q.length) return 20;
  }
  return 0;
}

/**
 * Cmd/Ctrl+K jump-to-page.
 *
 * The destination list comes from NavItemsContext — the sidebar's own
 * computed list — so the palette can never offer a route this user isn't
 * allowed to open.
 */
export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const { navItems } = useNavItems();

  const results = useMemo(
    () =>
      navItems
        .map((item) => ({ item, score: scoreMatch(query, item.label) }))
        .filter((r) => r.score > 0)
        // Stable within a score band, so an unfiltered list keeps sidebar order.
        .sort((a, b) => b.score - a.score)
        .map((r) => r.item),
    [navItems, query]
  );

  const close = useCallback(() => {
    setOpen(false);
    setQuery("");
    setActive(0);
  }, []);

  // Global shortcut. Registered once; bails out while a field has focus so
  // forms and the notebook composer keep their normal behaviour.
  useEffect(() => {
    const onKeyDown = (e) => {
      const isK = e.key === "k" || e.key === "K";
      if (isK && (e.metaKey || e.ctrlKey)) {
        if (!open && isTypingTarget(e.target)) return;
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  useEffect(() => {
    if (open) {
      setActive(0);
      // Focus after paint, or the input isn't mounted yet.
      const id = requestAnimationFrame(() => inputRef.current?.focus());
      return () => cancelAnimationFrame(id);
    }
    return undefined;
  }, [open]);

  // Keep the highlight inside the result list as it shrinks while typing.
  useEffect(() => {
    setActive((i) => (results.length === 0 ? 0 : Math.min(i, results.length - 1)));
  }, [results.length]);

  useBodyScrollLock(open);

  if (!open) return null;

  const go = (item) => {
    if (!item) return;
    close();
    navigate(item.to || item.path);
  };

  const onFieldKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      close();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => (results.length ? (i + 1) % results.length : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => (results.length ? (i - 1 + results.length) % results.length : 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      go(results[active]);
    }
  };

  return (
    <div
      role="presentation"
      onClick={close}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,23,42,0.45)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        paddingTop: "12vh",
        zIndex: 4000,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Jump to page"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(560px, calc(100% - 2rem))",
          background: "#fff",
          borderRadius: RADIUS.card,
          boxShadow: "0 24px 60px rgba(15,23,42,0.28)",
          overflow: "hidden",
        }}
      >
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onFieldKeyDown}
          placeholder="Jump to…"
          aria-label="Jump to page"
          style={{
            width: "100%",
            padding: "14px 16px",
            fontSize: "0.95rem",
            border: "none",
            borderBottom: `1px solid ${C.line}`,
            outline: "none",
            color: C.ink,
          }}
        />

        <div style={{ maxHeight: "50vh", overflowY: "auto", padding: 6 }}>
          {results.length === 0 ? (
            <div style={{ padding: "18px 12px", color: C.inkFaint, fontSize: "0.85rem", textAlign: "center" }}>
              Nothing matches “{query}”.
            </div>
          ) : (
            results.map((item, idx) => {
              const Icon = item.icon;
              const selected = idx === active;
              return (
                <button
                  key={item.to || item.path}
                  type="button"
                  onMouseEnter={() => setActive(idx)}
                  onClick={() => go(item)}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "9px 10px",
                    border: "none",
                    borderRadius: RADIUS.control,
                    background: selected ? C.accentSoft : "transparent",
                    color: selected ? C.accentInk : C.ink,
                    fontSize: "0.88rem",
                    fontWeight: selected ? 700 : 500,
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  {Icon ? <Icon style={{ fontSize: "1rem", flexShrink: 0 }} /> : null}
                  <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {item.label}
                  </span>
                  {selected ? (
                    <span style={{ fontFamily: MONO, fontSize: "0.62rem", color: C.inkFaint }}>↵</span>
                  ) : null}
                </button>
              );
            })
          )}
        </div>

        <div
          style={{
            display: "flex",
            gap: 14,
            padding: "7px 12px",
            borderTop: `1px solid ${C.line}`,
            background: C.sunken,
            fontFamily: MONO,
            fontSize: "0.62rem",
            color: C.inkFaint,
          }}
        >
          <span>↑↓ move</span>
          <span>↵ open</span>
          <span>esc close</span>
        </div>
      </div>
    </div>
  );
}
